import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
interface CaseStudy {
  id: string;
  shortTitle: string;
  title: string;
  description: string;
  tags: string[];
  from: string;
  to: string;
  highlights: string[];
  metrics?: {
    label: string;
    value: string;
  }[];
  accent: 'green' | 'blue' | 'navy' | 'amber' | 'purple';
}
const cases: CaseStudy[] = [{
  id: 'powerbuilder-ui',
  shortTitle: 'PowerBuilder UI Modernization',
  title: 'UI Modernization for PowerBuilder Applications',
  description: 'Transformed classical PowerBuilder user interfaces into modern enterprise-grade experiences using an AI-assisted multi-agent conversion pipeline. Achieved up to 95% first-pass conversion accuracy and improved go-to-market speed by 90%.',
  tags: ['PowerBuilder', 'AI Conversion', 'UI/UX'],
  from: 'PowerBuilder',
  to: 'Modern Web UI',
  highlights: ['AI-assisted multi-agent conversion pipeline', 'Enterprise-grade modern user experience', 'Dramatically faster go-to-market'],
  metrics: [{
    label: 'First-pass accuracy',
    value: '95%'
  }, {
    label: 'Faster GTM',
    value: '90%'
  }],
  accent: 'green'
}, {
  id: 'vb6-net-angular',
  shortTitle: 'VB6 to .NET & Angular',
  title: 'VB6 to .NET & Angular Migration',
  description: 'Modernized legacy logistics applications into a scalable .NET Core and Angular platform while preserving critical business logic and workflows. The transformed platform followed modern engineering standards, architectural best practices, and maintainability guidelines.',
  tags: ['VB6', '.NET Core', 'Angular'],
  from: 'VB6',
  to: '.NET + Angular',
  highlights: ['Preserved critical business logic and workflows', 'Modern engineering standards and best practices', 'Scalable, maintainable architecture'],
  accent: 'blue'
}, {
  id: 'accelq-playwright',
  shortTitle: 'AccelQ → Playwright Tests',
  title: 'AccelQ to Playwright Test Migration',
  description: 'Migrated enterprise automated test suites from AccelQ to Playwright using a custom-built AI-assisted conversion workflow. The solution automatically extracted AccelQ scripts and generated equivalent Playwright automation scripts at scale.',
  tags: ['AccelQ', 'Playwright', 'Test Automation'],
  from: 'AccelQ',
  to: 'Playwright',
  highlights: ['Custom AI-assisted conversion workflow', 'Automatic extraction and translation at scale', 'Equivalent Playwright automation scripts'],
  accent: 'purple'
}, {
  id: 'ai-test-generation',
  shortTitle: 'AI-Driven Test Generation',
  title: 'AI-Driven Test Generation from Source Code',
  description: 'Built an intelligent pipeline capable of generating technical specifications, functional documentation, and automated test cases directly from application source code. This enabled large-scale test automation initiatives even in environments with limited or outdated documentation.',
  tags: ['AI', 'Test Generation', 'Documentation'],
  from: 'Source Code',
  to: 'Specs + Tests',
  highlights: ['Generates specs, docs, and test cases from code', 'Works with limited or outdated documentation', 'Enables large-scale test automation'],
  accent: 'amber'
}, {
  id: 'powerbuilder-net-angular',
  shortTitle: 'PowerBuilder → .NET & Angular',
  title: 'PowerBuilder to .NET & Angular Modernization',
  description: 'Migrated multiple PowerBuilder modules to a modern web-based .NET and Angular architecture. The modernization improved scalability, maintainability, user experience, and long-term platform sustainability.',
  tags: ['PowerBuilder', '.NET', 'Angular', 'Architecture'],
  from: 'PowerBuilder',
  to: '.NET + Angular',
  highlights: ['Multiple modules migrated to modern web architecture', 'Improved scalability and maintainability', 'Long-term platform sustainability'],
  accent: 'navy'
}];
const accentMap: Record<CaseStudy['accent'], {
  bg: string;
  text: string;
  chip: string;
  bar: string;
}> = {
  green: {
    bg: 'bg-brand-green',
    text: 'text-brand-green',
    chip: 'bg-brand-green/10 text-brand-green',
    bar: 'bg-brand-green'
  },
  blue: {
    bg: 'bg-brand-blue',
    text: 'text-brand-blue',
    chip: 'bg-brand-blue/10 text-brand-blue',
    bar: 'bg-brand-blue'
  },
  navy: {
    bg: 'bg-brand-navy',
    text: 'text-brand-navy',
    chip: 'bg-brand-navy/10 text-brand-navy',
    bar: 'bg-brand-navy'
  },
  amber: {
    bg: 'bg-amber-500',
    text: 'text-amber-600',
    chip: 'bg-amber-500/10 text-amber-700',
    bar: 'bg-amber-500'
  },
  purple: {
    bg: 'bg-purple-600',
    text: 'text-purple-600',
    chip: 'bg-purple-500/10 text-purple-700',
    bar: 'bg-purple-600'
  }
};
export function CaseStudies() {
  const [activeId, setActiveId] = useState(cases[0].id);
  const active = cases.find(c => c.id === activeId) || cases[0];
  const a = accentMap[active.accent];
  return <section id="cases" className="relative py-24 bg-slate-50 overflow-hidden" data-id="element-12">
      <div className="absolute inset-0 pointer-events-none" data-id="element-13">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-brand-green/5 blur-3xl" data-id="element-14" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-brand-blue/5 blur-3xl" data-id="element-15" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-16">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14" data-id="element-17">
          <div className="max-w-2xl" data-id="element-18">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-green/10 text-brand-green text-xs font-semibold uppercase tracking-wider mb-5" data-id="element-19">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green" data-id="element-20" />
              Proven Outcomes
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-heading leading-tight" data-id="element-21">
              Real-World Modernization{' '}
              <span className="text-brand-green" data-id="element-22">Success Stories</span>
            </h2>
          </div>
          <p className="text-slate-600 text-base md:text-right max-w-md" data-id="element-23">
            Click a case study to explore how organizations have modernized
            legacy systems with BetterCode AI.
          </p>
        </motion.div>

        {/* Tab + Detail Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8" data-id="element-24">
          {/* Tab list */}
          <div className="lg:col-span-4" data-id="element-25">
            <div className="lg:sticky lg:top-28" data-id="element-26">
              <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible -mx-4 px-4 lg:mx-0 lg:px-0 pb-2 lg:pb-0" data-id="element-27">
                {cases.map((c, i) => {
                const isActive = c.id === activeId;
                const ca = accentMap[c.accent];
                return <button key={c.id} onClick={() => setActiveId(c.id)} className={`group relative flex-shrink-0 lg:flex-shrink text-left rounded-xl border transition-all duration-300 overflow-hidden ${isActive ? 'bg-white border-slate-200 shadow-md' : 'bg-white/40 border-transparent hover:bg-white hover:border-slate-200'}`} aria-pressed={isActive} data-id="element-28">
                      {/* Accent bar */}
                      <span className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 ${isActive ? ca.bar : 'bg-transparent group-hover:bg-slate-200'}`} data-id="element-29" />
                      <div className="px-4 py-4 lg:px-5 lg:py-4 flex items-center gap-3 min-w-[220px] lg:min-w-0" data-id="element-30">
                        <span className={`flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 font-bold text-xs font-heading transition-colors ${isActive ? `${ca.bg} text-white` : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200'}`} data-id="element-31">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className={`font-semibold text-sm leading-snug transition-colors ${isActive ? 'text-slate-900' : 'text-slate-500 group-hover:text-slate-700'}`} data-id="element-32">
                          {c.shortTitle}
                        </span>
                      </div>
                    </button>;
              })}
              </div>
            </div>
          </div>

          {/* Active case card */}
          <div className="lg:col-span-8" data-id="element-33">
            <AnimatePresence mode="wait" data-id="element-34">
              <motion.article key={active.id} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.35
            }} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-35">
                {/* Visual header */}
                <div className="relative bg-brand-navy p-8 lg:p-10 overflow-hidden" data-id="element-36">
                  <div className="absolute inset-0 opacity-30" style={{
                  backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                  backgroundSize: '24px 24px'
                }} data-id="element-37" />
                  <div className={`absolute -bottom-16 -right-16 w-64 h-64 rounded-full ${a.bg} opacity-25 blur-3xl`} data-id="element-38" />

                  <div className="relative flex flex-col gap-6" data-id="element-39">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white w-fit" data-id="element-40">
                      <span className={`w-1.5 h-1.5 rounded-full ${a.bg}`} data-id="element-41" />
                      Case Study
                    </span>

                    {/* Migration visual */}
                    <div className="flex items-center gap-3 text-sm font-semibold flex-wrap" data-id="element-42">
                      <div className="px-3.5 py-2 rounded-lg bg-white/10 backdrop-blur border border-white/15 text-white whitespace-nowrap" data-id="element-43">
                        {active.from}
                      </div>
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${a.bg} shadow-md flex-shrink-0`} data-id="element-44">
                        <ArrowRight className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-45" />
                      </div>
                      <div className="px-3.5 py-2 rounded-lg bg-white text-brand-navy whitespace-nowrap shadow-sm" data-id="element-46">
                        {active.to}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-8 lg:p-10" data-id="element-47">
                  <div className="flex flex-wrap gap-1.5 mb-4" data-id="element-48">
                    {active.tags.map(tag => <span key={tag} className={`px-2.5 py-0.5 text-[11px] font-medium rounded-md ${a.chip}`} data-id="element-49">
                        {tag}
                      </span>)}
                  </div>

                  <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 font-heading mb-4 leading-tight" data-id="element-50">
                    {active.title}
                  </h3>

                  <p className="text-slate-600 leading-relaxed mb-6 text-base lg:text-lg" data-id="element-51">
                    {active.description}
                  </p>

                  {/* Highlights */}
                  <ul className="space-y-2.5 mb-6" data-id="element-52">
                    {active.highlights.map(h => <li key={h} className="flex items-start gap-2.5 text-sm text-slate-700" data-id="element-53">
                        <span className={`flex-shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded-full ${a.chip}`} data-id="element-54">
                          <Check className={`w-3 h-3 ${a.text}`} strokeWidth={3} data-id="element-55" />
                        </span>
                        {h}
                      </li>)}
                  </ul>

                  {/* Metrics */}
                  {active.metrics && active.metrics.length > 0 && <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-56">
                      {active.metrics.map(m => <div key={m.label} className="rounded-xl bg-slate-50 border border-slate-100 p-4" data-id="element-57">
                          <div className={`text-3xl font-bold font-heading ${a.text}`} data-id="element-58">
                            {m.value}
                          </div>
                          <div className="text-xs text-slate-500 mt-1" data-id="element-59">
                            {m.label}
                          </div>
                        </div>)}
                    </div>}

                  <button className={`inline-flex items-center gap-1.5 text-sm font-semibold ${a.text} hover:gap-2.5 transition-all`} data-id="element-60">
                    Read full case study
                    <ArrowUpRight className="w-4 h-4" data-id="element-61" />
                  </button>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}